// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { AppIcon, Heading } from '@shared/ui-kit/components';
import {
  type ChangeEvent,
  type ChangeEventHandler,
  type FC,
  type PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { assetTypeMap } from '@shared/data-access/transformers/assets/group-positions-by-asset-type';
import { useTranslation } from 'react-i18next';

export const AssignTagsEditor: FC<
  PropsWithChildren<{
    assetsInput?: boolean;
    onAddTicker?: (ticker: string, assetType: string) => void;
  }>
> = ({ children, assetsInput, onAddTicker }) => {
  const { t } = useTranslation();
  const [ticker, setTicker] = useState('');
  /* istanbul ignore next */
  const onChangeValue: ChangeEventHandler<HTMLInputElement> = useCallback(e => {
    setTicker(e.target.value.toUpperCase());
  }, []);

  const onSelectValue = useCallback(
    /* istanbul ignore next */
    (e: ChangeEvent<HTMLInputElement>, value?: AssetsSearchResp) => {
      setTicker('');
      onAddTicker?.(
        e.target.value.toUpperCase(),
        assetTypeMap[value!.asset_type as keyof typeof assetTypeMap],
      );
    },
    [onAddTicker],
  );

  return (
    <div className="assign-tags-editor pt-8">
      <div className="flex gap-x-16">
        <Heading
          className="mt-1"
          title={t('tag-manager.assign-tags', { ns: 'components' })}
          variant="h3"
        />
        {assetsInput && (
          <div>
            <AutocompleteInput
              error={true}
              name="ticker"
              placeholder={t('tag-manager.add-assets', { ns: 'components' })}
              onChangeValue={onChangeValue}
              onSelectValue={onSelectValue}
              value={ticker}
              rightIcon={
                <AppIcon
                  icon="Add"
                  size="lg"
                  color="disabled"
                  className="hover:!text-primary"
                />
              }
              className="!rounded-full"
            />
          </div>
        )}
      </div>
      <div className="mt-7 grid grid-cols-[200px_100px_100px_200px_1fr] gap-2">
        <div className="col-span-1 ml-2 font-normal text-border-medium">
          {t('tag-manager.table-headers.name', { ns: 'components' })}
        </div>
        <div className="col-span-1 ml-2 font-normal text-border-medium">
          {t('tag-manager.table-headers.isin-ticker', { ns: 'components' })}
        </div>
        <div className="col-span-1 ml-2 font-normal text-border-medium">
          {t('tag-manager.table-headers.asset-class', { ns: 'components' })}
        </div>
        <div className="col-span-1 ml-2 font-normal text-border-medium">
          {t('tag-manager.table-headers.assign-tags', { ns: 'components' })}
        </div>
        <div className="col-span-1 ml-2 font-normal text-border-medium">
          {t('tag-manager.table-headers.tags', { ns: 'components' })}
        </div>
        {children}
      </div>
    </div>
  );
};
